import { useEffect, useState } from "react";
import { api, TYPE_META } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ImportedList from "@/components/ImportedList";

export default function Settings() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [bulk, setBulk] = useState("");
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [recent, setRecent] = useState([]);
  const [collections, setCollections] = useState([]);
  // "" = none, "__new__" = create-new, else existing collection id.
  const [collectionChoice, setCollectionChoice] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");

  useEffect(() => {
    // Both fetches are non-critical: the page still functions without stats
    // or collections. Log failures so real backend issues surface in devtools.
    api.get("/stats")
      .then(r => setStats(r.data))
      .catch(e => console.error("[settings] /stats failed:", e));
    api.get("/collections")
      .then(r => setCollections(r.data || []))
      .catch(e => console.error("[settings] /collections failed:", e));
  }, []);

  async function runImport(e) {
    e.preventDefault();
    if (!bulk.trim()) return toast.error("Paste some URLs or a list first");
    setImporting(true);
    setReport(null);
    try {
      const body = { text: bulk };
      if (collectionChoice === "__new__" && newCollectionName.trim()) {
        body.new_collection_name = newCollectionName.trim();
      } else if (collectionChoice && collectionChoice !== "__new__") {
        body.collection_id = collectionChoice;
      }
      const r = await api.post("/items/bulk-import", body);
      setReport(r.data);
      setRecent(r.data.items || []);
      const ext = r.data.collection_name ? ` → ${r.data.collection_name}` : "";
      toast.success(`Filed ${r.data.created} of ${r.data.urls_found}${ext}`);
      if (r.data.created > 0) {
        setBulk("");
        // Refresh collections so a freshly-created one appears in the picker.
        if (collectionChoice === "__new__") {
          try {
            const cr = await api.get("/collections");
            setCollections(cr.data || []);
            if (r.data.collection_id) setCollectionChoice(r.data.collection_id);
            setNewCollectionName("");
          } catch { /* not critical */ }
        }
      }
    } catch (e) {
      console.error("[settings:bulk-import] failed:", e);
      toast.error("Could not run import");
    } finally { setImporting(false); }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-12" data-testid="settings-page">
      <div>
        <span className="stamp">Settings</span>
        <h1 className="mt-4 font-serif-lit text-5xl tracking-tight text-[var(--ink)]">Your library, your way.</h1>
      </div>

      <section className="placed-card p-6">
        <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Account</h2>
        <div className="mt-4 flex items-center gap-4">
          {user?.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-[rgba(140,106,47,0.12)] object-cover"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}/>}
          <div>
            <p className="font-serif-lit text-lg text-[var(--ink)]">{user?.name}</p>
            <p className="font-mono-lc text-xs tracking-[0.16em] text-[var(--ink-dusty)] break-all">{user?.email}</p>
          </div>
        </div>
        <button onClick={logout} className="lc-btn-ghost mt-6" data-testid="settings-logout-btn">Sign out</button>
      </section>

      <section className="placed-card p-6" data-testid="bulk-import-section">
        <div className="flex items-center gap-3">
          <Upload size={16} strokeWidth={1.4} className="text-[var(--bronze)]"/>
          <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Bulk import</h2>
        </div>
        <p className="mt-2 font-garamond italic text-[var(--ink-dusty)]">
          Paste any list of links — a Letterboxd CSV, a Goodreads export, a Notion page, an email of bookmarks.
          We&apos;ll pull every <code className="font-mono-lc text-xs">https://</code> URL out and file each one.
        </p>
        <form onSubmit={runImport} className="mt-5 space-y-4">
          <textarea
            data-testid="bulk-import-textarea"
            value={bulk} onChange={e => setBulk(e.target.value)}
            placeholder="https://open.spotify.com/track/...&#10;https://letterboxd.com/film/parasite-2019/&#10;https://cooking.nytimes.com/recipes/..."
            rows={8}
            className="w-full p-4 bg-[var(--paper-aged)] border border-[rgba(42,30,20,0.10)] rounded font-mono-lc text-xs text-[var(--ink)] outline-none focus:border-[var(--bronze)] transition-colors"
            style={{ lineHeight: 1.6 }}
          />
          <div className="space-y-2" data-testid="bulk-import-collection-picker">
            <label className="font-cinzel text-[10px] tracking-[0.24em] uppercase text-[var(--ink-dusty)]">
              Also drop into a collection (optional)
            </label>
            <select
              data-testid="bulk-import-collection-select"
              value={collectionChoice}
              onChange={e => setCollectionChoice(e.target.value)}
              className="w-full bg-[var(--paper-aged)] border border-[rgba(42,30,20,0.14)] rounded px-3 py-2 font-garamond text-[15px] text-[var(--ink)] outline-none focus:border-[var(--bronze)]"
            >
              <option value="">— None, just file to shelves —</option>
              {collections.length > 0 && <option disabled>──────────</option>}
              {collections.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.item_count != null ? ` · ${c.item_count}` : ""}</option>
              ))}
              <option disabled>──────────</option>
              <option value="__new__">＋ Create a new collection…</option>
            </select>
            {collectionChoice === "__new__" && (
              <input
                data-testid="bulk-import-new-collection-input"
                value={newCollectionName}
                onChange={e => setNewCollectionName(e.target.value)}
                placeholder="e.g. Sunday mornings"
                className="w-full bg-[var(--paper-aged)] border border-[rgba(42,30,20,0.14)] rounded px-3 py-2 font-serif-lit text-[16px] text-[var(--ink)] outline-none focus:border-[var(--bronze)]"
              />
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button type="submit" className="lc-btn flex items-center gap-2" disabled={importing} data-testid="bulk-import-btn">
              <Upload size={13} strokeWidth={1.6}/> {importing ? "Filing…" : "Import"}
            </button>
            <p className="font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--ink-light)]">
              Up to 200 URLs per batch · YouTube playlists & Letterboxd/Goodreads CSVs auto-expand
            </p>
          </div>
        </form>
        {report && (
          <div className="mt-5 placed-card p-4 flex items-start gap-3" data-testid="bulk-import-report"
               style={{ borderColor: "var(--sage)" }}>
            <CheckCircle2 size={14} strokeWidth={1.6} className="text-[var(--sage)] mt-0.5"/>
            <div>
              <p className="font-cinzel text-[11px] tracking-[0.22em] uppercase" style={{ color: "var(--sage)" }}>
                Filed {report.created} of {report.urls_found}
              </p>
              {report.playlists_expanded > 0 && (
                <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                  {report.playlists_expanded} playlist{report.playlists_expanded === 1 ? "" : "s"} unrolled into individual videos.
                </p>
              )}
              {report.csv_detected && (
                <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                  CSV detected — titles, ratings and authors merged in.
                </p>
              )}
              {report.collection_name && (
                <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                  All dropped into the <em>{report.collection_name}</em> collection.
                </p>
              )}
              {report.skipped > 0 && (
                <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                  {report.skipped} URL{report.skipped === 1 ? "" : "s"} could not be read (paywalls or robots).
                </p>
              )}
            </div>
          </div>
        )}
        {recent.length > 0 && (
          <div className="mt-6">
            <ImportedList
              items={recent}
              onChange={(updated) => setRecent(prev => prev.map(i => i.id === updated.id ? updated : i))}
              onRemove={(id) => setRecent(prev => prev.filter(i => i.id !== id))}
            />
          </div>
        )}
      </section>

      {stats && (
        <section className="placed-card p-6" data-testid="library-stats">
          <h2 className="font-serif-lit text-2xl text-[var(--ink)]">A small inventory</h2>
          <p className="mt-2 text-[var(--ink-dusty)]">
            <span className="font-serif-lit text-3xl text-[var(--ink)] italic">{stats.total}</span> things kept so far.
          </p>
          <ul className="mt-4 grid grid-cols-2 gap-3">
            {Object.entries(stats.by_type).map(([k, v]) => (
              <li key={k} className="font-mono-lc text-[11px] tracking-[0.16em] uppercase text-[var(--ink-dusty)] flex justify-between border-b border-[rgba(42,30,20,0.06)] py-2">
                <span>{k}</span><span className="text-[var(--ink)]">{v}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="placed-card p-6">
        <h2 className="font-serif-lit text-2xl text-[var(--ink)]">About</h2>
        <p className="mt-3 text-[var(--ink-dusty)] font-garamond italic">
          A space built around what you love, not what an algorithm pushes.
          Your shelf stays yours. Share what you want, when you want.
        </p>
      </section>

      <ThemeSection />
      <PreferencesSection />
      <CommonsPrivacySection />
    </div>
  );
}

function ThemeSection() {
  // Theme is a UI-only preference stored in localStorage ("ce-theme") so the
  // bootstrap script in index.html can apply it before React mounts (no
  // flash of wrong palette on hard refresh). Initial state matches whatever
  // the bootstrap already applied to <html data-theme=...>.
  const [theme, setTheme] = useState(() =>
    typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "light"
      ? "light" : "dark"
  );
  const isLight = theme === "light";

  function toggle() {
    const next = isLight ? "dark" : "light";
    setTheme(next);
    try {
      if (next === "light") {
        document.documentElement.setAttribute("data-theme", "light");
        localStorage.setItem("ce-theme", "light");
      } else {
        document.documentElement.removeAttribute("data-theme");
        localStorage.setItem("ce-theme", "dark");
      }
      toast.success(next === "light" ? "lights on" : "lights off");
    } catch (e) {
      console.error("[settings:theme] toggle failed:", e);
    }
  }

  return (
    <section className="placed-card p-6" data-testid="theme-section">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Appearance</h2>
          <p className="mt-2 font-garamond italic text-sm text-[var(--ink-dusty)]">
            Dark is the designed baseline — warm charcoal and brass. Light is
            paper-aged for daytime reading.
          </p>
          <p className="mt-3 font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
            currently: {isLight ? "light" : "dark"}
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          role="switch"
          aria-checked={isLight}
          data-testid="theme-toggle"
          aria-label={isLight ? "switch to dark theme" : "switch to light theme"}
          className="relative shrink-0 mt-1 h-6 w-11 rounded-full border transition-colors overflow-hidden"
          style={{
            borderColor: isLight ? "var(--bronze)" : "rgba(42,30,20,0.24)",
            background: isLight ? "var(--bronze)" : "var(--switch-off-bg)",
          }}
        >
          <span
            className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-[var(--switch-thumb)] shadow-sm transition-[left] duration-150"
            style={{ left: isLight ? "calc(100% - 1rem - 3px)" : "3px" }}
          />
        </button>
      </div>
    </section>
  );
}

function CommonsPrivacySection() {
  // Two controls, both hitting the same PATCH /api/commons/profile endpoint
  // that Phase A already ships. Values: discoverable=bool,
  // who_can_request="everyone"|"none". No counts, no algorithms.
  const [discoverable, setDiscoverable] = useState(true);
  const [whoCanRequest, setWhoCanRequest] = useState("everyone");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(true);   // if false, invite them to claim first

  useEffect(() => {
    api.get("/commons/profile/me")
      .then(r => {
        setDiscoverable(r.data?.discoverable !== false);
        setWhoCanRequest(r.data?.who_can_request === "none" ? "none" : "everyone");
        setClaimed(Boolean(r.data?.username));
      })
      .catch(e => {
        // 401 is expected before login; log anything else.
        if (e?.response?.status !== 401) console.error("[settings:privacy] load failed:", e);
      })
      .finally(() => setLoaded(true));
  }, []);

  async function patch(fields) {
    setBusy(true);
    try {
      const r = await api.patch("/commons/profile", fields);
      setDiscoverable(r.data?.discoverable !== false);
      setWhoCanRequest(r.data?.who_can_request === "none" ? "none" : "everyone");
      toast.success("saved");
    } catch (e) {
      console.error("[settings:privacy] patch failed:", e);
      toast.error("could not save");
    }
    finally { setBusy(false); }
  }

  return (
    <section className="placed-card p-6" data-testid="commons-privacy-section">
      <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Commons Privacy</h2>
      <p className="mt-2 font-garamond italic text-sm text-[var(--ink-dusty)]">
        Control how you appear in the social layer. Both settings apply immediately.
      </p>

      {!loaded ? (
        <p className="mt-5 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--ink-dusty)]">loading…</p>
      ) : !claimed ? (
        <p className="mt-5 font-garamond italic text-[var(--ink-dusty)]">
          You haven&apos;t claimed a username yet. <a href="/commons" className="underline decoration-[var(--bronze)]/60 hover:text-[var(--bronze)]" data-testid="commons-privacy-claim-link">Visit The Commons</a> to pick one first.
        </p>
      ) : (
        <>
          {/* Discoverable toggle — mirrors the Today's Pick switch styling */}
          <div className="mt-6 flex items-start justify-between gap-6">
            <div className="flex-1">
              <p className="font-serif-lit text-lg text-[var(--ink)]">Show me in the directory</p>
              <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                When off, your profile is hidden from the alphabetical directory. Friends can still see you; anyone with your direct link sees a &ldquo;this member is private&rdquo; card.
              </p>
            </div>
            <button
              type="button"
              onClick={() => patch({ discoverable: !discoverable })}
              disabled={busy}
              data-testid="commons-discoverable-toggle"
              role="switch"
              aria-checked={discoverable}
              className="relative shrink-0 mt-1 h-6 w-11 rounded-full border transition-colors overflow-hidden disabled:opacity-60"
              style={{
                borderColor: discoverable ? "var(--bronze)" : "rgba(42,30,20,0.24)",
                background: discoverable ? "var(--bronze)" : "var(--switch-off-bg)",
              }}
            >
              <span
                className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-[var(--switch-thumb)] shadow-sm transition-[left] duration-150"
                style={{ left: discoverable ? "calc(100% - 1rem - 3px)" : "3px" }}
              />
            </button>
          </div>

          {/* Who-can-request radio — everyone vs no one */}
          <div className="mt-8">
            <p className="font-serif-lit text-lg text-[var(--ink)]">Who can send friend requests?</p>
            <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
              Choose &ldquo;no one&rdquo; if you&apos;d rather keep to yourself. Existing friendships aren&apos;t affected either way.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="commons-who-can-request">
              {[
                { key: "everyone", label: "anyone" },
                { key: "none",     label: "no one" },
              ].map(opt => {
                const active = whoCanRequest === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => patch({ who_can_request: opt.key })}
                    disabled={busy}
                    data-testid={`commons-who-can-request-${opt.key}`}
                    className={`chip ${active ? "active" : ""}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function PreferencesSection() {
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(r => setEnabled(r.data?.todays_pick_enabled !== false))
      .catch(e => console.error("[settings:preferences] load failed:", e));
  }, []);

  async function toggle() {
    setBusy(true);
    try {
      const r = await api.patch("/settings", { todays_pick_enabled: !enabled });
      setEnabled(r.data?.todays_pick_enabled !== false);
      toast.success(!enabled ? "Today's Pick is on" : "Today's Pick hidden");
    } catch (e) {
      console.error("[settings:preferences] patch failed:", e);
      toast.error("Could not save preference");
    }
    finally { setBusy(false); }
  }

  return (
    <section className="placed-card p-6" data-testid="preferences-section">
      <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Preferences</h2>
      <div className="mt-5 flex items-start justify-between gap-6">
        <div className="flex-1">
          <p className="font-serif-lit text-lg text-[var(--ink)]">Today&apos;s Pick</p>
          <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
            A random unwatched thing per shelf, on the library home. No weighting, no scoring —
            pure chance. Turn off if you want a quieter home.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          data-testid="todays-pick-toggle"
          role="switch"
          aria-checked={enabled}
          className="relative shrink-0 mt-1 h-6 w-11 rounded-full border transition-colors overflow-hidden disabled:opacity-60"
          style={{
            borderColor: enabled ? "var(--bronze)" : "rgba(42,30,20,0.24)",
            background: enabled ? "var(--bronze)" : "var(--switch-off-bg)",
          }}
        >
          <span
            className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-[var(--switch-thumb)] shadow-sm transition-[left] duration-150"
            style={{ left: enabled ? "calc(100% - 1rem - 3px)" : "3px" }}
          />
        </button>
      </div>
    </section>
  );
}
